import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
  styled,
  InputAdornment,
  Fade,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MoodIcon from "@mui/icons-material/Mood";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Form Paper estilizado com borda suave
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 6px 20px ${alpha("#B0BEC5", 0.1)}`,
  position: "relative",
  overflow: "hidden",
}));

// Ícone decorativo de coração
const HeartIcon = styled(FavoriteIcon)(({ theme }) => ({
  position: "absolute",
  fontSize: 100,
  opacity: 0.08,
  color: theme.palette.secondary.light,
  top: -20,
  right: -30,
  transform: "rotate(15deg)",
  pointerEvents: "none",
}));

export const HeroSection = () => {
  const theme = useTheme();
  const [formData, setFormData] = React.useState({
    nome: "",
    telefone: "",
    email: "",
    tipoPlano: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Dados do formulário:", formData);
    // Lógica para enviar os dados
  };

  return (
    <Box
      sx={{
        pt: { xs: 8, md: 10 },
        pb: { xs: 10, md: 12 },
        background: `linear-gradient(180deg, ${alpha(
          theme.palette.primary.light,
          0.1
        )} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Coluna de Texto */}
          <Grid item xs={12} md={6}>
            <Fade in={true} timeout={800}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <FamilyRestroomIcon
                    sx={{ color: theme.palette.secondary.main, mr: 1 }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.secondary.dark,
                    }}
                  >
                    Cuidado para toda a Família
                  </Typography>
                </Box>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2.5rem", sm: "3rem", md: "3.3rem" },
                    lineHeight: 1.25,
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  Planos de Saúde feitos com Carinho e Atenção
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                  }}
                >
                  Oferecemos planos completos com atendimento humanizado,
                  pensados para proteger quem você mais ama.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  href="#beneficios"
                  endIcon={<MoodIcon />}
                >
                  Conheça os Benefícios
                </Button>
              </Box>
            </Fade>
          </Grid>

          {/* Coluna do Formulário */}
          <Grid item xs={12} md={6} id="formulario">
            <Fade in={true} timeout={1000}>
              <FormPaper elevation={0}>
                <HeartIcon />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    textAlign: "center",
                    color: theme.palette.secondary.dark,
                  }}
                >
                  Receba uma proposta especial
                </Typography>

                <Box component="form" noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Seu nome"
                    variant="outlined"
                    margin="normal"
                    required
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonPinIcon
                            sx={{ color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Seu telefone"
                    variant="outlined"
                    margin="normal"
                    required
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    type="tel"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneInTalkIcon
                            sx={{ color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Seu e-mail (opcional)"
                    variant="outlined"
                    margin="normal"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon
                            sx={{ color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth margin="normal" sx={{ mb: 3 }}>
                    <InputLabel id="tipo-plano-label-soft">
                      Tipo de plano desejado
                    </InputLabel>
                    <Select
                      labelId="tipo-plano-label-soft"
                      id="tipo-plano-soft"
                      name="tipoPlano"
                      value={formData.tipoPlano}
                      label="Tipo de plano desejado"
                      onChange={(e) =>
                        handleChange(e as React.ChangeEvent<HTMLInputElement>)
                      }
                      startAdornment={
                        <InputAdornment position="start">
                          <ArticleOutlinedIcon
                            sx={{
                              ml: 0.5,
                              mr: 1,
                              color: theme.palette.action.active,
                            }}
                          />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="individual">Individual</MenuItem>
                      <MenuItem value="familiar">Familiar</MenuItem>
                      <MenuItem value="empresarial">Empresarial</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    type="submit"
                    color="primary"
                    endIcon={<ArrowForwardIosIcon fontSize="small" />}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                    }}
                  >
                    Enviar Solicitação
                  </Button>
                </Box>
              </FormPaper>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
